<template>
  <div class="space-y-6">
    <!-- Text Input -->
    <div>
      <label for="overlay-text" class="block text-sm font-medium text-gray-700 mb-1">
        Text Overlay
      </label>
      <textarea
        id="overlay-text"
        v-model="text"
        rows="3"
        class="w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
        placeholder="Enter your text here. Use [FirstName] to personalize."
      ></textarea>
      <p class="mt-1 text-xs text-gray-500">
        Use [FirstName] as a placeholder which will be replaced with the recipient's name
      </p>
    </div>
    
    <!-- Font Size -->
    <div>
      <div class="flex justify-between items-center mb-1">
        <label for="font-size" class="block text-sm font-medium text-gray-700">
          Font Size: {{ fontSize }}px
        </label>
      </div>
      <input
        id="font-size"
        v-model.number="fontSize"
        type="range"
        min="12"
        max="72"
        step="1"
        class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
      />
      <div class="flex justify-between text-xs text-gray-500 mt-1">
        <span>Small</span>
        <span>Large</span>
      </div>
    </div>
    
    <!-- Overlay Style -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">
        Overlay Style
      </label>
      <div class="grid grid-cols-3 gap-2">
        <button
          class="p-3 border rounded-md text-center transition-colors relative"
          :class="overlayStyle === 'white' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:bg-gray-50'"
          @click="setOverlayStyle('white')"
        >
          <div class="bg-white border border-gray-200 rounded-md p-2 text-xs text-black text-center shadow-sm">
            White
          </div>
          <div v-if="overlayStyle === 'white'" class="absolute -top-1 -right-1 bg-indigo-500 rounded-full p-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </div>
        </button>
        
        <button
          class="p-3 border rounded-md text-center transition-colors relative"
          :class="overlayStyle === 'translucent' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:bg-gray-50'"
          @click="setOverlayStyle('translucent')"
        >
          <div class="bg-black bg-opacity-50 rounded-md p-2 text-xs text-white text-center">
            Translucent
          </div>
          <div v-if="overlayStyle === 'translucent'" class="absolute -top-1 -right-1 bg-indigo-500 rounded-full p-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </div>
        </button>
        
        <button
          class="p-3 border rounded-md text-center transition-colors relative"
          :class="overlayStyle === 'branded' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:bg-gray-50'"
          @click="setOverlayStyle('branded')"
        >
          <div class="bg-blue-600 rounded-md p-2 text-xs text-white text-center">
            Branded
          </div>
          <div v-if="overlayStyle === 'branded'" class="absolute -top-1 -right-1 bg-indigo-500 rounded-full p-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </div>
        </button>
      </div>
    </div>
    
    <!-- Brand Color (only if style is branded) -->
    <div v-if="overlayStyle === 'branded'">
      <label for="brand-color" class="block text-sm font-medium text-gray-700 mb-1">
        Brand Color
      </label>
      <div class="flex items-center">
        <input
          id="brand-color"
          v-model="brandColor"
          type="color"
          class="h-10 w-10 border-0 p-0 rounded-md cursor-pointer"
        />
        <span class="ml-2 text-sm text-gray-600">{{ brandColor }}</span>
      </div>
    </div>
    
    <!-- Overlay Shape -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">
        Overlay Shape
      </label>
      <div class="grid grid-cols-3 gap-2">
        <button
          v-for="shape in overlayShapes"
          :key="shape.value"
          class="p-3 border rounded-md text-center transition-colors relative"
          :class="overlayShape === shape.value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:bg-gray-50'"
          @click="setOverlayShape(shape.value)"
        >
          <div :class="getShapePreviewClass(shape.value)" class="h-10 flex items-center justify-center text-xs">
            {{ shape.label }}
          </div>
          <div v-if="overlayShape === shape.value" class="absolute -top-1 -right-1 bg-indigo-500 rounded-full p-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </div>
        </button>
      </div>
    </div>
    
    <!-- Overlay Rotation -->
    <div>
      <div class="flex justify-between items-center mb-1">
        <label for="rotation" class="block text-sm font-medium text-gray-700">
          Rotation: {{ rotation }}°
        </label>
      </div>
      <input
        id="rotation"
        v-model.number="rotation"
        type="range"
        min="-180"
        max="180"
        step="1"
        class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
      />
      <div class="flex justify-between text-xs text-gray-500 mt-1">
        <span>-180°</span>
        <span>0°</span>
        <span>+180°</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

export default {
  name: 'OverlayCustomizer',
  data() {
    return {
      overlayShapes: [
        { value: 'rectangle', label: 'Rectangle' },
        { value: 'circle', label: 'Circle' },
        { value: 'speech-left', label: 'Speech Left' },
        { value: 'speech-right', label: 'Speech Right' },
        { value: 'ribbon', label: 'Ribbon' },
        { value: 'hexagon', label: 'Hexagon' }
      ]
    };
  },
  computed: {
    ...mapState({
      overlaySettingsState: state => state.overlaySettings
    }),
    
    text: {
      get() {
        return this.overlaySettingsState.text;
      },
      set(value) {
        this.UPDATE_OVERLAY_TEXT(value);
      }
    },
    
    fontSize: {
      get() {
        return this.overlaySettingsState.fontSize;
      },
      set(value) {
        this.UPDATE_OVERLAY_FONT_SIZE(value);
      }
    },
    
    overlayStyle: {
      get() {
        return this.overlaySettingsState.style;
      },
      set(value) {
        this.UPDATE_OVERLAY_STYLE(value);
      }
    },
    
    overlayShape: {
      get() {
        return this.overlaySettingsState.shape;
      },
      set(value) {
        this.UPDATE_OVERLAY_SHAPE(value);
      }
    },
    
    brandColor: {
      get() {
        return this.overlaySettingsState.brandColor;
      },
      set(value) {
        this.UPDATE_BRAND_COLOR(value);
      }
    },
    
    rotation: {
      get() {
        return this.overlaySettingsState.rotation;
      },
      set(value) {
        this.UPDATE_OVERLAY_ROTATION(value);
      }
    }
  },
  methods: {
    ...mapMutations([
      'UPDATE_OVERLAY_TEXT',
      'UPDATE_OVERLAY_FONT_SIZE',
      'UPDATE_OVERLAY_STYLE',
      'UPDATE_OVERLAY_SHAPE',
      'UPDATE_BRAND_COLOR',
      'UPDATE_OVERLAY_ROTATION'
    ]),
    
    setOverlayStyle(style) {
      this.UPDATE_OVERLAY_STYLE(style);
    },
    
    setOverlayShape(shape) {
      this.UPDATE_OVERLAY_SHAPE(shape);
    },
    
    getShapePreviewClass(shape) {
      const baseClasses = 'bg-gray-200 text-gray-800 w-full ';
      
      switch (shape) {
        case 'rectangle':
          return baseClasses + 'rounded-md';
        case 'circle':
          return baseClasses + 'rounded-full';
        case 'speech-left':
          return baseClasses + 'rounded-md speech-left';
        case 'speech-right':
          return baseClasses + 'rounded-md speech-right';
        case 'ribbon':
          return baseClasses + 'ribbon';
        case 'hexagon':
          return baseClasses + 'hexagon';
        default:
          return baseClasses + 'rounded-md';
      }
    }
  }
};
</script>

<style scoped>
.speech-left, .speech-right {
  position: relative;
}

.speech-left:before {
  content: '';
  position: absolute;
  left: -5px;
  top: 50%;
  margin-top: -5px;
  border: 5px solid transparent;
  border-right-color: #E5E7EB;
}

.speech-right:after {
  content: '';
  position: absolute;
  right: -5px;
  top: 50%;
  margin-top: -5px;
  border: 5px solid transparent;
  border-left-color: #E5E7EB;
}

.ribbon {
  position: relative;
  padding: 0 5px;
}

.ribbon:before, .ribbon:after {
  content: '';
  position: absolute;
  top: -2px;
  border: 2px solid transparent;
}

.ribbon:before {
  left: 0;
  border-right-color: #9CA3AF;
  border-bottom-color: #9CA3AF;
}

.ribbon:after {
  right: 0;
  border-left-color: #9CA3AF;
  border-bottom-color: #9CA3AF;
}

.hexagon {
  clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
}
</style>